import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarDays, MapPin, Users, IndianRupee, CheckCircle, Loader2, Download, Eye, CreditCard, HelpCircle, X, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentEvents() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [enrollments, setEnrollments] = useState({});
  const [rsvps, setRsvps] = useState({}); // event_id -> rsvp
  const [savingRsvp, setSavingRsvp] = useState({}); // event_id -> bool
  const [loading, setLoading] = useState(true);
  const [enrollDialog, setEnrollDialog] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState(null);
  const [form, setForm] = useState({ participant_name: '', participant_email: '', participant_phone: '', team_name: '' });

  useEffect(() => {
    if (!document.getElementById('razorpay-script')) {
      const s = document.createElement('script'); s.id = 'razorpay-script'; s.src = 'https://checkout.razorpay.com/v1/checkout.js'; s.async = true; document.head.appendChild(s);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const r = await api.get('/events');
      setEvents(r.data);
      const enrollMap = {};
      const rsvpMap = {};
      for (const ev of r.data) {
        try {
          const er = await api.get(`/events/${ev.id}/my-enrollment`);
          if (er.data.enrolled) enrollMap[ev.id] = er.data;
        } catch {}
        try {
          const rr = await api.get(`/events/${ev.id}/my-rsvp`);
          if (rr.data?.rsvp) rsvpMap[ev.id] = rr.data.rsvp;
        } catch {}
      }
      setEnrollments(enrollMap);
      setRsvps(rsvpMap);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleRsvp = async (event_id, status) => {
    setSavingRsvp(prev => ({ ...prev, [event_id]: true }));
    try {
      const r = await api.post(`/events/${event_id}/rsvp`, { status });
      setRsvps(prev => ({ ...prev, [event_id]: r.data.rsvp }));
      // Update participant_count locally
      setEvents(prev => prev.map(e => e.id === event_id ? { ...e, participant_count: r.data.attending_count } : e));
      toast.success({ attending: "You're attending! 🎉", maybe: 'Marked as maybe', not_attending: 'RSVP updated' }[status]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to RSVP');
    } finally {
      setSavingRsvp(prev => ({ ...prev, [event_id]: false }));
    }
  };

  const openEnrollDialog = (event) => {
    setForm({ participant_name: user?.name || '', participant_email: user?.email || '', participant_phone: '', team_name: '' });
    setEnrollDialog(event);
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!form.participant_name || !form.participant_email) { toast.error('Name and email required'); return; }
    const event = enrollDialog;

    if (event.entry_fee > 0) {
      // Phase 86 — Payment flow with proper transaction tracking
      setEnrolling(true);
      try {
        const orderRes = await api.post(`/events/${event.id}/pay`);
        const order = orderRes.data;
        const transaction_id = order.transaction_id;

        if (order.mock || order.amount === 0) {
          // Mock mode (no live Razorpay keys configured) — short-circuit through /register
          // so the registration row is still written with payment_status='paid'.
          const regRes = await api.post(`/events/${event.id}/register`, {
            ...form,
            transaction_id,
            razorpay_order_id: order.order_id,
            razorpay_payment_id: `mock_pay_${Math.random().toString(36).slice(2, 12)}`,
          });
          toast.success('Enrolled successfully!');
          await showReceipt(event, regRes.data);
        } else {
          // Live Razorpay Checkout
          if (!window.Razorpay) { toast.error('Payment SDK not loaded. Refresh and try again.'); setEnrolling(false); return; }
          const options = {
            key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
            name: 'Event Registration', description: event.title,
            prefill: { name: form.participant_name, email: form.participant_email, contact: form.participant_phone || '' },
            handler: async (response) => {
              try {
                const regRes = await api.post(`/events/${event.id}/register`, {
                  ...form,
                  transaction_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                });
                toast.success('Payment successful! You\'re enrolled.');
                await showReceipt(event, regRes.data);
              } catch (err) {
                const msg = err.response?.data?.detail || 'Enrollment failed after payment. Please contact the academy.';
                toast.error(msg);
              }
              setEnrolling(false);
            },
            modal: { ondismiss: () => { setEnrolling(false); toast.info('Payment cancelled'); } },
            theme: { color: '#10B981' }
          };
          const rzp = new window.Razorpay(options);
          rzp.on('payment.failed', (r) => { toast.error(`Payment failed: ${r.error.description}`); setEnrolling(false); });
          rzp.open();
          return;
        }
      } catch (err) {
        const msg = err.response?.data?.detail || 'Payment could not be initialized. Please try again.';
        toast.error(msg);
      }
      setEnrolling(false);
    } else {
      // Free event
      setEnrolling(true);
      try {
        const regRes = await api.post(`/events/${event.id}/register`, form);
        toast.success('Enrolled successfully!');
        await showReceipt(event, regRes.data);
      } catch (err) { toast.error(err.response?.data?.detail || 'Enrollment failed'); }
      setEnrolling(false);
    }
  };

  const showReceipt = async (event, regData) => {
    setEnrollDialog(null);
    // Fetch academy info
    let acadInfo = {};
    try {
      const er = await api.get(`/events/${event.id}/my-enrollment`);
      if (er.data.enrolled) acadInfo = er.data;
    } catch {}
    setReceiptDialog({ event, registration: regData, form: { ...form }, academy: acadInfo });
    setForm({ participant_name: '', participant_email: '', participant_phone: '', team_name: '' });
    fetchEvents();
  };

  const viewEnrollmentReceipt = async (event) => {
    const data = enrollments[event.id];
    if (!data) return;
    setReceiptDialog({
      event,
      registration: { registration_id: data.registration?.id || '' },
      form: { participant_name: data.registration?.participant_name || '', participant_email: data.registration?.participant_email || '', participant_phone: data.registration?.participant_phone || '', team_name: data.registration?.team_name || '' },
      academy: data
    });
  };

  const downloadReceipt = () => {
    if (!receiptDialog) return;
    const { event, form: f, registration, academy } = receiptDialog;
    const c = document.createElement('canvas'); c.width = 650; c.height = 440;
    const x = c.getContext('2d');
    x.fillStyle = '#f0fdf4'; x.fillRect(0, 0, 650, 440);
    x.fillStyle = '#0f766e'; x.fillRect(0, 0, 650, 6);
    // Academy name
    x.fillStyle = '#0f766e'; x.font = 'bold 20px sans-serif'; x.fillText(academy?.academy_name || 'Academy', 30, 45);
    x.fillStyle = '#64748b'; x.font = '11px sans-serif';
    if (academy?.academy_phone) x.fillText(academy.academy_phone, 30, 62);
    if (academy?.academy_email) x.fillText(academy.academy_email, 30, 77);
    // Title
    x.fillStyle = '#0f766e'; x.font = 'bold 16px sans-serif'; x.fillText('ENROLLMENT RECEIPT', 400, 45);
    x.fillStyle = '#16a34a'; x.font = 'bold 13px sans-serif'; x.fillText('\u2713 ENROLLED', 430, 65);
    // Event
    x.fillStyle = '#1e293b'; x.font = 'bold 16px sans-serif'; x.fillText(event.title, 30, 115);
    x.fillStyle = '#64748b'; x.font = '12px sans-serif';
    x.fillText(`Date: ${event.date}${event.end_date && event.end_date !== event.date ? ' \u2014 ' + event.end_date : ''}`, 30, 135);
    if (event.venue) x.fillText(`Venue: ${event.venue}`, 30, 152);
    // Participant
    x.fillStyle = '#0f766e'; x.font = 'bold 13px sans-serif'; x.fillText('Participant Details', 30, 190);
    x.fillStyle = '#1e293b'; x.font = '12px sans-serif';
    x.fillText(`Name: ${f.participant_name}`, 30, 212);
    x.fillText(`Email: ${f.participant_email}`, 30, 230);
    if (f.participant_phone) x.fillText(`Phone: ${f.participant_phone}`, 30, 248);
    if (f.team_name) x.fillText(`Team: ${f.team_name}`, 30, 266);
    // Registration
    x.fillStyle = '#0f766e'; x.font = 'bold 13px sans-serif'; x.fillText('Registration ID', 30, 305);
    x.fillStyle = '#1e293b'; x.font = '11px monospace'; x.fillText(registration.registration_id || registration.id || '', 30, 325);
    // Fee
    if (event.entry_fee > 0) { x.fillStyle = '#0f766e'; x.font = 'bold 15px sans-serif'; x.fillText(`Entry Fee: \u20b9${event.entry_fee}`, 400, 305); x.fillStyle = '#16a34a'; x.font = 'bold 12px sans-serif'; x.fillText('PAID', 400, 325); }
    // Footer
    x.fillStyle = '#94a3b8'; x.font = '9px sans-serif'; x.fillText('Powered by Out-Play.in', 270, 420);
    const link = document.createElement('a'); link.download = `enrollment-${event.title.replace(/\s+/g, '-')}.png`; link.href = c.toDataURL('image/png'); link.click();
    toast.success('Receipt downloaded');
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid md:grid-cols-2 gap-4">{[1,2].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div></div>;

  return (
    <div className="space-y-6" data-testid="student-events-page">
      <div><h1 className="text-2xl font-display font-semibold">Events</h1><p className="text-sm text-muted-foreground">View and enroll in academy events</p></div>

      {events.length === 0 ? (
        <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16"><CalendarDays className="h-12 w-12 text-muted-foreground/40 mb-4" /><h3 className="text-lg font-semibold mb-1">No events yet</h3></CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {events.map(event => {
            const isEnrolled = !!enrollments[event.id];
            return (
              <Card key={event.id} className="hover:shadow-md transition-shadow" data-testid={`event-card-${event.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <Badge className={`text-xs mb-2 border-0 ${event.status === 'upcoming' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{event.status}</Badge>
                      <h3 className="font-semibold text-base">{event.title}</h3>
                    </div>
                    {isEnrolled && <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle className="h-3 w-3 mr-1" /> Enrolled</Badge>}
                  </div>
                  {event.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>}
                  <div className="space-y-1.5 text-sm mb-4">
                    <div className="flex items-center gap-2 text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" />{event.date}{event.end_date && event.end_date !== event.date ? ` — ${event.end_date}` : ''}</div>
                    {event.venue && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{event.venue}</div>}
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1 text-muted-foreground"><Users className="h-3.5 w-3.5" />{event.participant_count}{event.max_participants ? `/${event.max_participants}` : ''}</span>
                      {event.entry_fee > 0 && <span className="flex items-center gap-1 text-muted-foreground"><IndianRupee className="h-3.5 w-3.5" />₹{event.entry_fee}</span>}
                    </div>
                  </div>
                  {isEnrolled ? (
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => viewEnrollmentReceipt(event)}><Eye className="h-4 w-4 mr-1" /> View Receipt</Button>
                      <Button variant="outline" className="flex-1" onClick={() => { viewEnrollmentReceipt(event); setTimeout(downloadReceipt, 300); }}><Download className="h-4 w-4 mr-1" /> Download</Button>
                    </div>
                  ) : (
                    <>
                      {/* Phase 78 — Quick RSVP block (separate from formal enrollment + payment) */}
                      <div className="mb-3 pb-3 border-b">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Quick RSVP</p>
                          {rsvps[event.id] && (
                            <Badge className={`text-[10px] border-0 ${
                              rsvps[event.id].status === 'attending' ? 'bg-emerald-100 text-emerald-700' :
                              rsvps[event.id].status === 'maybe' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {rsvps[event.id].status === 'attending' ? "You're attending" : rsvps[event.id].status === 'maybe' ? 'Maybe' : "Can't attend"}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          <Button
                            size="sm" variant={rsvps[event.id]?.status === 'attending' ? 'default' : 'outline'}
                            className={`h-7 text-[11px] ${rsvps[event.id]?.status === 'attending' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                            disabled={savingRsvp[event.id]} onClick={() => handleRsvp(event.id, 'attending')}
                            data-testid={`rsvp-yes-${event.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Going
                          </Button>
                          <Button
                            size="sm" variant={rsvps[event.id]?.status === 'maybe' ? 'default' : 'outline'}
                            className={`h-7 text-[11px] ${rsvps[event.id]?.status === 'maybe' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                            disabled={savingRsvp[event.id]} onClick={() => handleRsvp(event.id, 'maybe')}
                            data-testid={`rsvp-maybe-${event.id}`}
                          >
                            <HelpCircle className="h-3 w-3 mr-1" /> Maybe
                          </Button>
                          <Button
                            size="sm" variant={rsvps[event.id]?.status === 'not_attending' ? 'default' : 'outline'}
                            className={`h-7 text-[11px] ${rsvps[event.id]?.status === 'not_attending' ? 'bg-slate-500 hover:bg-slate-600' : ''}`}
                            disabled={savingRsvp[event.id]} onClick={() => handleRsvp(event.id, 'not_attending')}
                            data-testid={`rsvp-no-${event.id}`}
                          >
                            <X className="h-3 w-3 mr-1" /> No
                          </Button>
                        </div>
                      </div>
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => openEnrollDialog(event)} data-testid="enroll-button">
                        {event.entry_fee > 0 ? <><CreditCard className="h-4 w-4 mr-1" /> Enroll & Pay ₹{event.entry_fee}</> : 'Enroll Now'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Enroll Dialog */}
      <Dialog open={!!enrollDialog} onOpenChange={() => setEnrollDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Enroll in {enrollDialog?.title}</DialogTitle></DialogHeader>
          {enrollDialog && (
            <form onSubmit={handleEnroll} className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/50"><p className="text-sm font-medium">{enrollDialog.title}</p><p className="text-xs text-muted-foreground">{enrollDialog.date} | {enrollDialog.venue}</p>{enrollDialog.entry_fee > 0 && <p className="text-xs font-semibold text-emerald-600 mt-1">Entry Fee: ₹{enrollDialog.entry_fee}</p>}</div>
              <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={form.participant_name} onChange={e => setForm({...form, participant_name: e.target.value})} required /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={form.participant_email} onChange={e => setForm({...form, participant_email: e.target.value})} required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.participant_phone} onChange={e => setForm({...form, participant_phone: e.target.value})} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Team</Label><Input value={form.team_name} onChange={e => setForm({...form, team_name: e.target.value})} /></div>
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={enrolling}>
                {enrolling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : enrollDialog.entry_fee > 0 ? <CreditCard className="h-4 w-4 mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                {enrolling ? 'Processing...' : enrollDialog.entry_fee > 0 ? `Pay ₹${enrollDialog.entry_fee} & Enroll` : 'Confirm Enrollment'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={!!receiptDialog} onOpenChange={() => setReceiptDialog(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="event-receipt-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /> Enrollment Confirmed</DialogTitle></DialogHeader>
          {receiptDialog && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                <p className="text-sm font-bold text-emerald-800">{receiptDialog.academy?.academy_name || 'Academy'}</p>
                {receiptDialog.academy?.academy_phone && <p className="text-xs text-emerald-600">{receiptDialog.academy.academy_phone}</p>}
                {receiptDialog.academy?.academy_email && <p className="text-xs text-emerald-600">{receiptDialog.academy.academy_email}</p>}
              </div>
              <div className="text-center">
                <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-1" />
                <p className="font-semibold">{receiptDialog.event.title}</p>
                <p className="text-sm text-muted-foreground">{receiptDialog.event.date} | {receiptDialog.event.venue}</p>
              </div>

              {/* Phase 86 — Ticket QR for matchday check-in */}
              {receiptDialog.registration?.registration_id && receiptDialog.registration?.ticket_code && (
                <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 p-4" data-testid="event-ticket-qr-block">
                  <div className="flex items-center gap-1.5 mb-2 justify-center">
                    <Ticket className="h-4 w-4 text-emerald-700" />
                    <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Matchday Ticket</p>
                  </div>
                  <div className="flex items-center justify-center bg-white rounded-lg p-2 shadow-inner">
                    <img
                      src={`${BACKEND_URL}/api/events/${receiptDialog.event.id}/registrations/${receiptDialog.registration.registration_id}/qr?t=${localStorage.getItem('outplay_token') || ''}`}
                      alt="Ticket QR"
                      className="h-40 w-40"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      data-testid="event-ticket-qr-img"
                    />
                  </div>
                  <p className="text-center text-[11px] font-mono mt-2 text-emerald-800 break-all" data-testid="event-ticket-code">{receiptDialog.registration.ticket_code}</p>
                  <p className="text-center text-[10px] text-muted-foreground mt-1">Show this at the gate to check in.</p>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Name</span><span className="font-medium">{receiptDialog.form.participant_name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Email</span><span className="font-medium">{receiptDialog.form.participant_email}</span></div>
                {receiptDialog.form.participant_phone && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Phone</span><span className="font-medium">{receiptDialog.form.participant_phone}</span></div>}
                {receiptDialog.event.entry_fee > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Entry Fee</span><span className="font-semibold text-emerald-600">₹{receiptDialog.event.entry_fee} — Paid</span></div>}
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Registration</span><span className="font-mono text-xs">{receiptDialog.registration?.registration_id || receiptDialog.registration?.id || ''}</span></div>
              </div>
              <p className="text-center text-[10px] text-muted-foreground">Powered by Out-Play.in</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { const w = window.open('', '_blank'); w.document.write(`<html><head><title>Enrollment Receipt</title></head><body style='font-family:sans-serif;max-width:500px;margin:40px auto;padding:20px'><h3 style='color:#0f766e'>${receiptDialog.academy?.academy_name||'Academy'}</h3><p style='font-size:12px;color:#64748b'>${receiptDialog.academy?.academy_phone||''}<br>${receiptDialog.academy?.academy_email||''}</p><hr/><h2>${receiptDialog.event.title}</h2><p>Date: ${receiptDialog.event.date} | Venue: ${receiptDialog.event.venue||''}</p><hr/><p><strong>Name:</strong> ${receiptDialog.form.participant_name}</p><p><strong>Email:</strong> ${receiptDialog.form.participant_email}</p>${receiptDialog.form.participant_phone?'<p><strong>Phone:</strong> '+receiptDialog.form.participant_phone+'</p>':''}<p><strong>Registration:</strong> ${receiptDialog.registration?.registration_id||receiptDialog.registration?.id||''}</p>${receiptDialog.registration?.ticket_code?'<p><strong>Ticket Code:</strong> '+receiptDialog.registration.ticket_code+'</p>':''}${receiptDialog.event.entry_fee>0?'<p><strong>Fee: ₹'+receiptDialog.event.entry_fee+' — PAID</strong></p>':''}<p style='color:#16a34a;font-weight:bold;font-size:18px'>✓ ENROLLED</p><p style='font-size:10px;color:#94a3b8;text-align:center;margin-top:30px'>Powered by Out-Play.in</p></body></html>`); w.document.close(); }}>
                  <Eye className="h-4 w-4 mr-1" /> View
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={downloadReceipt}><Download className="h-4 w-4 mr-1" /> Download PNG</Button>
              </div>
              {receiptDialog.registration?.registration_id && (
                <Button variant="outline" className="w-full" onClick={() => window.open(`/events/${receiptDialog.event.id}/registrations/${receiptDialog.registration.registration_id}/gatepass`, '_blank')} data-testid="receipt-print-gatepass">
                  <Ticket className="h-4 w-4 mr-1" /> Print Gate Pass
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
